import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, doc, getDoc, setDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@services/firebase';
import { Upload, X, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const ACCENT_COLORS = ['teal', 'purple', 'blue', 'navy', 'orange'];

const DEFAULT = {
  title:       '',
  description: '',
  iconUrl:     '',
  emoji:       '',
  accentColor: 'teal',
  featured:    false,
  ctaLabel:    '',
  ctaLink:     '',
  order:       10,
  visible:     true,
};

export default function AdminFeatureEditor() {
  const { id }    = useParams();
  const isEdit    = Boolean(id);
  const navigate  = useNavigate();

  const [form, setForm]           = useState(DEFAULT);
  const [loading, setLoading]     = useState(isEdit);
  const [saving, setSaving]       = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!isEdit) return;
    getDoc(doc(db, 'features', id)).then(snap => {
      if (snap.exists()) setForm({ ...DEFAULT, ...snap.data() });
    }).finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleImageUpload = async (file) => {
    if (!file) return;
    const docId   = id ?? `feat_${Date.now()}`;
    const path    = `feature-media/${docId}/${file.name}`;
    const storRef = ref(storage, path);
    const task    = uploadBytesResumable(storRef, file);
    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        snap => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        err  => { console.error(err.code, err); toast.error('Upload failed: ' + err.code); reject(err); },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          setUploadPct(null);
          resolve(url);
        },
      );
    });
  };

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await handleImageUpload(file);
      set('iconUrl', url);
    } catch { /* already toasted */ }
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const docId = id ?? doc(collection(db, 'features')).id;
      const data  = {
        ...form,
        order:     Number(form.order),
        updatedAt: serverTimestamp(),
        ...(isEdit ? {} : { createdAt: serverTimestamp() }),
      };
      if (isEdit) {
        await updateDoc(doc(db, 'features', docId), data);
      } else {
        await setDoc(doc(db, 'features', docId), data);
      }
      toast.success(isEdit ? 'Feature updated' : 'Feature created');
      navigate('/admin/features');
    } catch (err) {
      console.error(err);
      toast.error('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="h-64 flex items-center justify-center text-gray-400">Loading…</div>
  );

  return (
      <div className="max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/admin/features')} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Feature' : 'New Feature'}</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Icon Upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-4">Icon / Image</h2>

            {form.iconUrl && (
              <div className="relative inline-block mb-4">
                <img src={form.iconUrl} alt="icon" className="w-24 h-24 object-cover rounded-xl border" />
                <button type="button" onClick={() => set('iconUrl', '')}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <input type="file" ref={fileRef} accept="image/*" onChange={handleFilePick} className="hidden" />

            <div className="flex gap-3">
              <button type="button" onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-teal-400 hover:text-teal-600 transition-colors">
                <Upload className="w-4 h-4" />
                Upload Icon
              </button>
            </div>

            {uploadPct !== null && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-teal-500 h-2 rounded-full transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">{uploadPct}% uploaded</p>
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Or use Emoji (shown when no image)</label>
              <input
                type="text"
                value={form.emoji}
                onChange={e => set('emoji', e.target.value)}
                placeholder="e.g. 🚀 ⚡ 🎯"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-xl"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Or paste image URL</label>
              <input
                type="url"
                value={form.iconUrl}
                onChange={e => set('iconUrl', e.target.value)}
                placeholder="https://…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
              />
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-800">Content</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                value={form.title}
                onChange={e => set('title', e.target.value)}
                placeholder="e.g. Stunning Websites That Convert"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                rows={3}
                value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="Short description shown on the feature card…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
            </div>
          </div>

          {/* Style */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-800">Style</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
              <div className="flex gap-3">
                {ACCENT_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set('accentColor', c)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize border-2 transition-colors ${
                      form.accentColor === c
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="featured"
                checked={form.featured}
                onChange={e => set('featured', e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded"
              />
              <label htmlFor="featured" className="text-sm font-medium text-gray-700">
                Featured style (large icon, colored left border, hover effects)
              </label>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-800">Call to Action Button</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Label</label>
                <input
                  type="text"
                  value={form.ctaLabel}
                  onChange={e => set('ctaLabel', e.target.value)}
                  placeholder="e.g. View Examples"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                <input
                  type="text"
                  value={form.ctaLink}
                  onChange={e => set('ctaLink', e.target.value)}
                  placeholder="/projects or https://…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <h2 className="font-semibold text-gray-800">Settings</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={e => set('order', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.visible}
                    onChange={e => set('visible', e.target.checked)}
                    className="w-4 h-4 text-teal-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Visible on homepage</span>
                </label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pb-8">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Feature'}
            </button>
            <button type="button" onClick={() => navigate('/admin/features')}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
  );
}
